import { acceptHMRUpdate, defineStore } from "pinia";
import { clamp, forEach, reduce, cloneDeep } from "lodash-es";
import { v4 as uuidv4 } from "uuid";
import { splitUid, toUid } from "@/helpers/utility/Id";

import Simulation from "@editor/items/simulation/Simulation";

import { useModelStore } from "@editor/items/model/model.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useWayStore } from "@editor/items/way/ways.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useElements } from "@editor/items/element/elements";
import type { Uid } from "@/helpers/utility/Id";
import type { RelationItem } from "@editor/items/relation/RelationItem";
import type { ActionPlan } from "@editor/items/simulationRound/ActionPlan";
import type { ScheduledEffectItem } from "@editor/items/scheduledEffect/ScheduledEffectItem";
import type { ExpressionValue } from "@editor/items/expression/expressionFunctions";

const subviews = ["matrix", "ways"] as const;
const waysSubviews = ["waysAndCycles", "impulseTracking"] as const;

export type StateChange = {
  id: number;
  value: number;
  active: boolean;
};

type InspectorViewStoreState = {
  subview: "matrix" | "ways";
  waysSubview: "waysAndCycles" | "impulseTracking" | null;
  gridDrawerOpen: boolean;
  waysDrawerOpen: boolean;

  targetStateChanges: {
    [id: number]: StateChange;
  };
  targetStateChangesOrder: number[];

  resourceStateChanges: {
    [id: number]: StateChange;
  };
  resourceStateChangesOrder: number[];

  maxWayLength: number;
  wayStartItemId: number | null;
  wayStartItemType: "target" | "resource";
  wayEndItemId: number | null;
  wayEndItemType: "target" | "resource";
  wayShown: number;
  priorityMode: "slope" | "maxInfluence";

  selectedImpactMatrixId: number;
};

export const useInspectorView = defineStore("model/inspectorView", {
  state: () => {
    const s: InspectorViewStoreState = {
      subview: subviews[0],
      waysSubview: waysSubviews[0],
      gridDrawerOpen: true,
      waysDrawerOpen: false,

      targetStateChanges: {},
      targetStateChangesOrder: [],

      resourceStateChanges: {},
      resourceStateChangesOrder: [],

      maxWayLength: 5,
      wayStartItemId: null,
      wayStartItemType: "target",
      wayEndItemId: null,
      wayEndItemType: "target",
      wayShown: 0,
      priorityMode: "slope",

      selectedImpactMatrixId: 0
    };
    return s;
  },

  getters: {
    elementValues(state): { [uid: ValueUid]: ExpressionValue } {
      const values = cloneDeep(useElements().elementValues);

      /** MATRIX VIEW
       * The matrix view uses the current state of the simulation as base
       * for the element values.
       * When no simulation is loaded, the state of the elements can also
       * be modified.
       */

      // MATRIX subview
      if (state.subview == "matrix") {
        const simulationViewStore = useSimulationViewStore();
        const simulation = simulationViewStore.simulation;
        // Use the state of the simulation if there is one:
        if (simulation) {
          return simulationViewStore.elementValues;
        }
        // Otherwise, use the current values of the elements + state changes
        else {
          for (const resourceId in state.resourceStateChanges) {
            const resourceStateChange =
              state.resourceStateChanges[resourceId] || {};
            if (!resourceStateChange.active) continue;
            const uid = `resource_${resourceId}`;
            const valueGroup = values[uid];

            const { minValue, maxValue } = valueGroup;
            const value = Number(valueGroup.value) + resourceStateChange.value;
            if (minValue != null && maxValue != null) {
              valueGroup.value = clamp(value, minValue, maxValue);
            } else {
              valueGroup.value = value;
            }
          }

          for (const targetId in state.targetStateChanges) {
            const targetStateChange = state.targetStateChanges[targetId] || {};
            if (!targetStateChange.active) continue;

            const uid = `target_${targetId}`;
            const valueGroup = values[uid];

            const { minValue, maxValue } = valueGroup;
            const value = Number(valueGroup.value) + targetStateChange.value;
            if (minValue != null && maxValue != null) {
              valueGroup.value = clamp(value, minValue, maxValue);
            } else {
              valueGroup.value = value;
            }
          }

          return values;
        }
      }

      // WAYS subview
      else {
        if (state.waysSubview == "impulseTracking") {
          const way = useWayStore().active;
          if (!way) return values;

          const wayStartItemUid = way.startItemType + "_" + way.startItemId;
          const valueGroup = values[wayStartItemUid];
          if (valueGroup) {
            const { minValue, maxValue } = valueGroup;
            const value = Number(valueGroup.value) + way.startItemChange;
            if (minValue != null && maxValue != null) {
              valueGroup.value = clamp(value, minValue, maxValue);
            } else {
              valueGroup.value = value;
            }
          }
        }

        return values;
      }
    },

    allWaysBetweenSelectedItems(state): {
      id: number;
      items: (ItemIdentifier<"target"> | ItemIdentifier<"resource">)[];
      relationIds: number[];
      inverse: boolean;
      type: "way";
    }[] {
      if (state.wayStartItemId === null) return [];
      if (state.wayEndItemId === null) return [];
      const wayStartItem = {
        uid: toUid(state.wayStartItemType, state.wayStartItemId),
        id: state.wayStartItemId,
        type: state.wayStartItemType
      } as (ItemIdentifier<"target"> | ItemIdentifier<"resource">);
      const wayEndItem = {
        uid: toUid(state.wayEndItemType, state.wayEndItemId),
        id: state.wayEndItemId,
        type: state.wayEndItemType
      } as (ItemIdentifier<"target"> | ItemIdentifier<"resource">);

      interface WayToBuild {
        items: (ItemIdentifier<"target"> | ItemIdentifier<"resource">)[];
        relationIds: number[];
        inverse: boolean;
        type: "way";
      }

      let openWays: WayToBuild[] = [
        {
          items: [wayStartItem],
          relationIds: [] as number[],
          inverse: false,
          type: "way"
        }
      ];

      const finalWays: WayToBuild[] = [];

      function addToFinalIfReachedEndItem(way: WayToBuild) {
        const lastWayItem = way.items[way.items.length - 1];
        if (
          lastWayItem.type === wayEndItem.type &&
          lastWayItem.id == wayEndItem.id
        ) {
          finalWays.push(way);
          return true;
        }
        return false;
      }

      // loop go through the relations and add the ways
      const relationStore = useRelationStore();
      const relationsByStartUid = relationStore.activeByStartUid;

      const startUid = `${wayStartItem.type}_${wayStartItem.id}`;

      // No outgoing relations on the start element?
      // Stop right here!
      if (relationsByStartUid[startUid] == null) {
        return [];
      }

      function followWays(
        oldWays: WayToBuild[],
        relationsByStartUid: {
          [id: string]: RelationItem[];
        }
      ) {
        const stoppedWays: WayToBuild[] = [];
        let cyclingWays: WayToBuild[] = [];
        let openWays: WayToBuild[] = [];

        // go through the ways and get the ways and cycles there
        for (const way of oldWays) {
          const { cycles, open } = followWay(way, relationsByStartUid);
          if (open.length == 0 && cycles.length == 0) {
            stoppedWays.push(way);
          } else {
            openWays = openWays.concat(open);
          }

          cyclingWays = cyclingWays.concat(cycles);
        }

        return {
          ended: stoppedWays,
          open: openWays,
          cycles: cyclingWays
        };
      }

      /**
       */
      function followWay(
        way: WayToBuild,
        relationsByStartUid: {
          [id: string]: RelationItem[];
        }
      ) {
        if (!way || way.items.length == 0) {
          console.warn("no way passed.");
          console.trace();
          return { open: [], cycles: [] };
        }

        const lastItem = way.items[way.items.length - 1];
        const newWays: WayToBuild[] = [];
        const cycles: WayToBuild[] = [];
        const lastItemUid = `${lastItem.type}_${lastItem.id}`;
        const relations = relationsByStartUid[lastItemUid] || [];

        // go through the relations and build up all chains.
        for (const relation of relations) {
          if (relation.endItemType == "resource") continue;
          const endItemType: "target" = relation.endItemType;
          const endItemId: number = relation.endItemId;

          // the way changes inverse state every time a new inverse relation is added.
          const inverse = relation.inverse ? !way.inverse : way.inverse;

          const newWay: WayToBuild = {
            type: "way",
            items: [
              ...way.items,
              {
                id: endItemId,
                type: endItemType,
                uid: `${endItemType}_${endItemId}`
              }
            ],
            relationIds: [...way.relationIds, relation.id],
            inverse: inverse
          };

          // already in the way? a cycle!
          if (
            way.items.some((i) => i.type == endItemType && i.id == endItemId)
          ) {
            cycles.push(newWay);
          } else {
            newWays.push(newWay);
          }
        }

        return {
          open: newWays,
          cycles
        };
      }

      for (let i = 0; i < state.maxWayLength; i++) {
        const { open, cycles, ended } = followWays(
          openWays,
          relationsByStartUid
        );

        openWays = [];

        cycles.forEach((way) => addToFinalIfReachedEndItem(way));
        ended.forEach((way) => addToFinalIfReachedEndItem(way));
        open.forEach(
          (way) => addToFinalIfReachedEndItem(way) || openWays.push(way)
        );

        // console.log(`openWays in iteration ${i}:`, openWays);

        // stop the loop if every way was followed to the end.
        if (openWays.length == 0) break;
      }

      // console.log("openWays", openWays);
      // console.log("finalWays", finalWays);

      return finalWays.map((way, index) => {
        return {
          ...way,
          id: index
        };
      });
    },

    wayBetweenSelectedItems(state): {
      id: number;
      items: (ItemIdentifier<"target"> | ItemIdentifier<"resource">)[];
      relationIds: number[];
      inverse: boolean;
      type: "way";
    } | null {
      const wayShown = state.wayShown;
      const ways = this.allWaysBetweenSelectedItems;
      if (ways.length == 0) return null;
      return ways[wayShown];
    },

    /**
     * Calculated impulses based on the changes to the element start states
     */
    itemStateChangeSimulation(state): Simulation {
      const modelState = useModelStore().simulationModelState;

      const targetStore = useTargetStore();
      const resourceStore = useResourceStore();

      // calculate the impulses that would result of the
      const startTargetValues = targetStore.values;
      const startResourceValues = resourceStore.values;

      let simulationLength = 1;

      const roundActionPlans: { [roundIndex: number]: ActionPlan } = {
        0: Simulation.createRoundActionPlan()
      };

      // if we're in the single ways view,
      // simulate only the relations contained in the way
      // and the possible next relations
      if (state.waysSubview == "impulseTracking") {
        const wayStore = useWayStore();
        const activeWay = wayStore.active;
        if (activeWay) {
          const wayLength = activeWay.relationIds.length;
          simulationLength = wayLength + 2;

          // limit the activatable relations for each round to the ones
          // that are part of the way in that round
          for (let i = 0; i < wayLength; i++) {
            // every relation in the way is added to the simulated round
            const actionPlan = Simulation.createRoundActionPlan();
            actionPlan.allowedRelationIds = [activeWay.relationIds[i]];
            roundActionPlans[i + 1] = actionPlan;
          }

          // make sure the LAST round has an actionPlan
          if (roundActionPlans[wayLength] == null) {
            roundActionPlans[wayLength] = Simulation.createRoundActionPlan();
          }

          // add ALL possible next relations ONLY for the last round.
          const lastWayItem = wayStore.getLastItem(activeWay.id);
          const lastWayItemUid = `${lastWayItem?.type}_${lastWayItem?.id}`;
          const possibleWayRelations =
            useRelationStore().activeByStartUid[lastWayItemUid] || [];

          // get the ids of the way
          const relIds = roundActionPlans[wayLength].allowedRelationIds || [];

          relIds.push(...possibleWayRelations.map((r) => r.id));
        }
      }

      // Create scheduled impulses for the first round
      const scheduledFirstRoundEffects = reduce(
        this.elementValues,
        (list, valueGroup, _elementUid) => {
          const elementUid = _elementUid as ValueUid;
          const { type: elementType, id: elementId } = splitUid(elementUid);

          if (elementType !== "target" && elementType !== "resource")
            return list;

          const elementValue = Number(valueGroup.value);
          const startValues =
            elementType === "target" ? startTargetValues : startResourceValues;
          const amount = elementValue - startValues[elementId];

          if (amount === 0) return list;

          const id = uuidv4();
          list.push({
            id,
            uid: `sef-${id}`,
            activatorId: id,
            activatorType: "scheduledEffect",
            affectedType: elementType,
            affectedId: elementId,
            impulseExpressions: [amount < 0 ? `${amount}` : `+${amount}`],
            type: "scheduledEffect",
            createdAt: Date.now(),
            hint: "",
            visibility: "visible",
            pendingEffectsVisible: true,
            pendingEffectsOpenable: true
          });
          return list;
        },
        [] as ScheduledEffectItem[]
      );
      roundActionPlans[0].scheduledEffects = scheduledFirstRoundEffects;

      const startElementValues = {
        ...targetStore.expressionValues,
        ...resourceStore.expressionValues
      };

      // create the impulses by simulation
      const simulation: Simulation = new Simulation(
        {
          startElementValues,
          maxRounds: simulationLength,
          useDelays: false,
          actionPlans: roundActionPlans
        },
        Simulation.loadGameStoreData(modelState)
      );

      simulation.simulateRelations(simulationLength);
      console.log("simulated relations", simulation);

      return simulation;
    }
  },

  actions: {
    setStateChangeTargets(targetIds: number[]) {
      const newChanges: {
        [targetId: number]: { id: number; value: number; active: boolean };
      } = {};

      const newIds: number[] = [];

      // update list of changes
      for (const targetId of targetIds) {
        if (!this.targetStateChanges[targetId]) {
          newChanges[targetId] = { id: targetId, value: 0, active: true };
          newIds.push(targetId);
        } else {
          newChanges[targetId] = this.targetStateChanges[targetId];
        }
      }
      this.set({ targetStateChanges: newChanges });

      // update change order: remove deleted target Ids and add new target ids
      // clear deleted changes from change order
      const clearedChangeOrder = this.targetStateChangesOrder.filter(
        (targetId) => newChanges[targetId] != null
      );
      const newChangeOrder = clearedChangeOrder.concat(newIds);
      this.set({ targetStateChangesOrder: newChangeOrder });
    },
    hideAllTargetValueChanges() {
      forEach(this.targetStateChanges, (change) => {
        if (change.active) {
          this.setTargetStateChange({
            ...change,
            active: false
          });
        }
      });
    },
    setPriorityMode(mode: "slope" | "maxInfluence") {
      this.set({ priorityMode: mode });
    },

    set(options: Partial<InspectorViewStoreState>) {
      this.$patch((state) => {
        Object.assign(state, options);
      });
    },
    setTargetStateChange(change: {
      id: number;
      value: number;
      active: boolean;
    }) {
      const changes = this.targetStateChanges;
      changes[change.id] = change;
    },
    removeTargetStateChange(changeId: number) {
      delete this.targetStateChanges[changeId];

      // remove it from order
      const order = this.targetStateChangesOrder;
      const index = order.indexOf(changeId);
      if (index > -1) {
        order.splice(index, 1);
      }
    }
  }
});

type ItemIdentifier<T extends "target" | "resource"> = {
  id: number;
  type: T;
  uid: Uid<T>;
};

type ValueUid = Uid<'target' | 'resource' | 'index' | 'flow' | 'flowBranch'>;

if (import.meta.hot) {
  import.meta.hot.accept(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    acceptHMRUpdate(useInspectorView as any, import.meta.hot)
  );
}
